import { useState, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { adminChargeDebuggingCollection } from '@/reducers/admin/adminChargeDebugging'
import { RootState } from '@/reducers'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
  TooltipItem,
} from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import 'chartjs-adapter-date-fns'
import { Line } from 'react-chartjs-2'
import { match } from 'ts-pattern'
import {
  ChargeDebuggingData,
  DemandResponseEventData,
  PlugSession,
  ChargeScheduleCommandLog,
  ChargeDebuggingChargeSchedule,
  ChargeScheduleTrip,
} from '@/types/admin/charge-debugging-data/chargeDebuggingData'
import LoadingText from '@/components/LoadingText'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
  annotationPlugin,
)

type AdminVehicle = {
  id: number
  car: {
    id: number
    display_name: string | null
    car_model?: {
      friendly_name: string
    }
  }
}

type AdminCharger = {
  id: number
  name: string
  authentication_inactive: boolean
}

type DeviceMode = 'vehicle' | 'charger'

type ChargeDebuggingProps = {
  userEmail?: string
  profileID?: string
}

function getCommandColor(commandType: string): string {
  return match(commandType)
    .with('start_charge', () => '#3b82f6') // blue - distinct from green plug-in
    .with('stop_charge', () => '#f97316') // orange - distinct from red unplug
    .with('set_charge_limit', () => '#f59e0b')
    .with('start_climate_control', () => '#06b6d4')
    .with('stop_climate_control', () => '#64748b')
    .with('set_schedule', () => '#8b5cf6')
    .otherwise(() => '#6b7280')
}

function getCommandLabel(commandType: string): string {
  return match(commandType)
    .with('start_charge', () => 'Start Charge')
    .with('stop_charge', () => 'Stop Charge')
    .with('set_charge_limit', () => 'Set Charge Limit')
    .with('start_climate_control', () => 'Start Climate')
    .with('stop_climate_control', () => 'Stop Climate')
    .with('set_schedule', () => 'Set Schedule')
    .otherwise(() => commandType)
}

function getBrowserTimezoneLabel(): string {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const now = new Date()
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'short',
  })
  const parts = formatter.formatToParts(now)
  const tzAbbr = parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
  return `${tz} (${tzAbbr})`
}

function formatDateForInput(date: Date): string {
  return date.toISOString().slice(0, 16)
}

function getDefaultStartDate(): string {
  const date = new Date()
  date.setDate(date.getDate() - 7)
  return formatDateForInput(date)
}

function getDefaultEndDate(): string {
  return formatDateForInput(new Date())
}

function computeRatePlanTimelineData(
  data: ChargeDebuggingData | undefined,
  fetchParams: { start: string; end: string } | null,
): {
  userRateData: { x: Date; y: number | null }[]
  programRateData: { x: Date; y: number | null }[]
} {
  if (!data || !fetchParams) {
    return { userRateData: [], programRateData: [] }
  }

  const startTime = new Date(fetchParams.start).getTime()
  const endTime = new Date(fetchParams.end).getTime()

  // Create hourly data points across the entire time range
  const userRateData: { x: Date; y: number | null }[] = []
  const programRateData: { x: Date; y: number | null }[] = []

  // Generate hourly time points
  for (let time = startTime; time <= endTime; time += 60 * 60 * 1000) {
    // 1 hour intervals
    const currentTime = new Date(time)
    const hour = currentTime.getHours()
    const isWeekend = currentTime.getDay() === 0 || currentTime.getDay() === 6

    // Find user rate for this hour
    const userRates = data.user_rate_plan.filter(
      (rate) => rate.hour === hour && rate.is_weekend === isWeekend,
    )
    const userAvgRate =
      userRates.length > 0
        ? userRates.reduce((sum, rate) => sum + rate.rate_cents_per_kwh, 0) /
          userRates.length
        : null

    // Find program rate for this hour
    const programRates = data.program_rate_plan.filter(
      (rate) => rate.hour === hour && rate.is_weekend === isWeekend,
    )
    const programAvgRate =
      programRates.length > 0
        ? programRates.reduce((sum, rate) => sum + rate.rate_cents_per_kwh, 0) /
          programRates.length
        : null

    userRateData.push({ x: currentTime, y: userAvgRate })
    programRateData.push({ x: currentTime, y: programAvgRate })
  }

  return { userRateData, programRateData }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function ChargeDebugging(_props: ChargeDebuggingProps) {
  const vehicles = useSelector(
    (state: RootState) =>
      (state.admin as { vehicles?: AdminVehicle[] }).vehicles ?? null,
  )
  const chargers = useSelector(
    (state: RootState) =>
      (state.admin as { chargers?: AdminCharger[] }).chargers ?? null,
  )
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('vehicle')
  const [vehicleId, setVehicleId] = useState<string>('')
  const [chargerId, setChargerId] = useState<string>('')
  const [startDate, setStartDate] = useState<string>(getDefaultStartDate())
  const [endDate, setEndDate] = useState<string>(getDefaultEndDate())
  const [fetchParams, setFetchParams] = useState<{
    vehicle_id?: string
    charger_id?: string
    start: string
    end: string
    include?: string
  } | null>(null)

  // Chart visibility toggles
  const [showChargeState, setShowChargeState] = useState(true)
  const [showChargeLimit, setShowChargeLimit] = useState(true)
  const [showPlugSessions, setShowPlugSessions] = useState(true)
  const [showCommands, setShowCommands] = useState(true)
  const [showUserRates, setShowUserRates] = useState(true)
  const [showProgramRates, setShowProgramRates] = useState(true)
  const [showEmissions, setShowEmissions] = useState(true)
  const [showDemandResponse, setShowDemandResponse] = useState(true)
  const [showChargerPower, setShowChargerPower] = useState(true)
  const [visibleCommandTypes, setVisibleCommandTypes] = useState<Set<string>>(
    new Set(),
  )

  const {
    data: fetchedData,
    isLoading,
    status,
  } = adminChargeDebuggingCollection.useFetch({
    params: fetchParams ?? undefined,
    require: fetchParams !== null,
    refetchOnChange: true,
  })

  const hasFetched = fetchParams !== null && status !== 'idle'

  const handleFetch = () => {
    const deviceId = deviceMode === 'vehicle' ? vehicleId : chargerId
    if (!deviceId || !startDate || !endDate) {
      return
    }
    const start = new Date(startDate).toISOString()
    const end = new Date(endDate).toISOString()

    // Build include list based on visible toggles for performance optimization
    const includeList: string[] = []
    if (showChargeState) includeList.push('charge_states')
    if (showPlugSessions) includeList.push('plug_sessions')
    if (showCommands)
      includeList.push('commands', 'charge_schedules', 'charge_schedule_trips')
    if (showUserRates) includeList.push('user_rate_plan')
    if (showProgramRates) includeList.push('program_rate_plan')
    if (showEmissions) includeList.push('emissions_data')
    if (showDemandResponse) includeList.push('demand_response_events')

    setFetchParams({
      ...(deviceMode === 'vehicle'
        ? { vehicle_id: vehicleId }
        : { charger_id: chargerId }),
      start,
      end,
      ...(includeList.length > 0 && { include: includeList.join(',') }),
    })
  }

  const data = fetchedData?.[0] as ChargeDebuggingData | undefined

  // Initialize visible command types when data changes
  useEffect(() => {
    if (data?.commands) {
      const types = new Set(data.commands.map((c) => c.command_type))
      setVisibleCommandTypes(types)
    }
  }, [data])

  const ratePlanTimelineData = useMemo(() => {
    return computeRatePlanTimelineData(data, fetchParams)
  }, [data, fetchParams])

  const emissionsTimelineData = useMemo(() => {
    if (!data || !data.emissions_data || data.emissions_data.length === 0) {
      return []
    }

    // Convert emissions data to chart format
    const emissionsData: {
      x: Date
      y: number | null
      intensity: number | null
    }[] = []

    data.emissions_data.forEach((emission) => {
      const startTime = new Date(emission.start_time)
      const endTime = new Date(emission.end_time)

      // Add data points at start and end of each emission period
      emissionsData.push({
        x: startTime,
        y: emission.intensity,
        intensity: emission.intensity,
      })
      emissionsData.push({
        x: endTime,
        y: emission.intensity,
        intensity: emission.intensity,
      })
    })

    return emissionsData.sort((a, b) => a.x.getTime() - b.x.getTime())
  }, [data])

  const drEventAnnotations = useMemo(() => {
    if (!data?.demand_response_events || !showDemandResponse) return {}
    const annotations: Record<string, object> = {}
    data.demand_response_events.forEach((event, idx) => {
      const startTime = new Date(event.start_datetime)
      const endTime = new Date(event.end_datetime)
      const label =
        event.name ??
        `${event.dispatch_event_type ?? event.event_type} DR Event`
      const statusParts: string[] = []
      if (event.enrolled) statusParts.push('Enrolled')
      if (event.participated) statusParts.push('Participated')
      if (event.user_opt_choice)
        statusParts.push(`Opt: ${event.user_opt_choice}`)

      annotations[`dr_event_${idx}`] = {
        type: 'box',
        xMin: startTime,
        xMax: endTime,
        backgroundColor: event.enrolled
          ? 'rgba(168, 85, 247, 0.15)'
          : 'rgba(107, 114, 128, 0.1)',
        borderColor: event.enrolled ? '#a855f7' : '#6b7280',
        borderWidth: 1,
        label: {
          display: true,
          content: [label, ...statusParts],
          position: { x: 'start', y: 'start' },
          backgroundColor: event.enrolled ? '#a855f7' : '#6b7280',
          color: '#ffffff',
          font: { size: 10, weight: 'bold' as const },
          padding: 4,
        },
      }
    })
    return annotations
  }, [data, showDemandResponse])

  const chartData = useMemo(() => {
    if (!data) {
      return null
    }

    const { plug_sessions, charge_states, commands, charge_schedules } = data

    // Early return if there's no data to display
    if (plug_sessions.length === 0 && charge_states.length === 0) {
      return null
    }

    const pluggedInData: { x: Date; y: number | null }[] = []
    const chargeStateData: {
      x: Date
      y: number | null
      chargingState: string | null
    }[] = []
    const chargeLimitData: { x: Date; y: number | null }[] = []

    plug_sessions.forEach((ps) => {
      pluggedInData.push({ x: new Date(ps.start_time), y: 100 })
      if (ps.end_time) {
        pluggedInData.push({ x: new Date(ps.end_time), y: 100 })
        pluggedInData.push({ x: new Date(ps.end_time), y: null })
      }
    })

    charge_states.forEach((cs) => {
      chargeStateData.push({
        x: new Date(cs.created),
        y: cs.battery_level ?? null,
        chargingState: cs.charging_state,
      })
    })

    // Use fetchParams start/end as x bounds for charge limit data
    if (fetchParams) {
      const minX = new Date(fetchParams.start).getTime()
      const maxX = new Date(fetchParams.end).getTime()

      // Filter schedules with valid battery_target_max
      const validSchedules = charge_schedules?.filter(
        (s) => s.battery_target_max !== null,
      )

      if (validSchedules?.length > 0) {
        // Find schedule for minX point: closest history_date <= minX, or closest overall if none
        const schedulesBeforeOrAtMin = validSchedules.filter(
          (s) => new Date(s.history_date).getTime() <= minX,
        )
        const minSchedule =
          schedulesBeforeOrAtMin.length > 0
            ? schedulesBeforeOrAtMin.reduce((closest, s) => {
                const sTime = new Date(s.history_date).getTime()
                const closestTime = new Date(closest.history_date).getTime()
                return Math.abs(sTime - minX) < Math.abs(closestTime - minX)
                  ? s
                  : closest
              })
            : validSchedules.reduce((closest, s) => {
                const sTime = new Date(s.history_date).getTime()
                const closestTime = new Date(closest.history_date).getTime()
                return Math.abs(sTime - minX) < Math.abs(closestTime - minX)
                  ? s
                  : closest
              })

        // Find schedule for maxX point: closest history_date >= maxX, or closest overall if none
        const schedulesAfterOrAtMax = validSchedules.filter(
          (s) => new Date(s.history_date).getTime() >= maxX,
        )
        const maxSchedule =
          schedulesAfterOrAtMax.length > 0
            ? schedulesAfterOrAtMax.reduce((closest, s) => {
                const sTime = new Date(s.history_date).getTime()
                const closestTime = new Date(closest.history_date).getTime()
                return Math.abs(sTime - maxX) < Math.abs(closestTime - maxX)
                  ? s
                  : closest
              })
            : validSchedules.reduce((closest, s) => {
                const sTime = new Date(s.history_date).getTime()
                const closestTime = new Date(closest.history_date).getTime()
                return Math.abs(sTime - maxX) < Math.abs(closestTime - maxX)
                  ? s
                  : closest
              })

        // Add point at minX using the minSchedule's battery_target_max
        chargeLimitData.push({
          x: new Date(minX),
          y: minSchedule.battery_target_max,
        })

        // Add points for schedules within the range
        validSchedules.forEach((schedule) => {
          const historyTime = new Date(schedule.history_date).getTime()
          if (historyTime > minX && historyTime < maxX) {
            chargeLimitData.push({
              x: new Date(schedule.history_date),
              y: schedule.battery_target_max,
            })
          }
        })

        // Add point at maxX using the maxSchedule's battery_target_max
        chargeLimitData.push({
          x: new Date(maxX),
          y: maxSchedule.battery_target_max,
        })
      }
    }

    const commandAnnotations: Record<
      string,
      {
        type: string
        xMin: Date
        xMax: Date
        borderColor: string
        borderWidth: number
        borderDash?: number[]
        display?: boolean
        enter?: (ctx: unknown, event: unknown) => boolean
        leave?: (ctx: unknown, event: unknown) => boolean
        label?: {
          display: boolean
          content: string | string[]
          position: string
          backgroundColor: string
          color: string
          font: { size: number; weight?: 'bold' | 'normal' }
          padding: number
        }
      }
    > = {}
    commands.forEach((cmd, idx) => {
      let timestamp: Date | null = null
      if (cmd.command_input) {
        try {
          const parsed = JSON.parse(cmd.command_input)
          if (parsed.timestamp) {
            timestamp = new Date(parsed.timestamp)
          }
        } catch {
          // ignore parse errors
        }
      }
      if (!timestamp && cmd.decision) {
        try {
          const decision = JSON.parse(cmd.decision)
          if (decision.timestamp) {
            timestamp = new Date(decision.timestamp)
          }
        } catch {
          // ignore
        }
      }
      // Fallback to created timestamp if no timestamp found in command_input or decision
      if (!timestamp) {
        timestamp = new Date(cmd.created)
      }

      const commandLabel = getCommandLabel(cmd.command_type)
      const commandTimestamp = timestamp.toLocaleString()
      commandAnnotations[`cmd_${idx}`] = {
        type: 'line',
        xMin: timestamp,
        xMax: timestamp,
        borderColor: getCommandColor(cmd.command_type),
        borderWidth: 2,
        borderDash: [4, 4],
        enter: (ctx: unknown, _event: unknown) => {
          const annotation = (
            ctx as {
              element?: {
                label?: { options?: { content?: string | string[] } }
              }
            }
          ).element
          if (annotation?.label?.options) {
            annotation.label.options.content = [
              commandLabel,
              '',
              commandTimestamp,
            ]
          }
          return true
        },
        leave: (ctx: unknown, _event: unknown) => {
          const annotation = (
            ctx as {
              element?: {
                label?: { options?: { content?: string | string[] } }
              }
            }
          ).element
          if (annotation?.label?.options) {
            annotation.label.options.content = commandLabel
          }
          return true
        },
        label: {
          display: true,
          content: commandLabel,
          position: '90%',
          backgroundColor: getCommandColor(cmd.command_type),
          color: '#111827',
          font: { size: 10, weight: 'bold' as const },
          padding: 4,
        },
      }
    })

    plug_sessions.forEach((ps, idx) => {
      const startTime = new Date(ps.start_time)
      const endTime = ps.end_time ? new Date(ps.end_time) : null
      const duration = endTime
        ? Math.round((endTime.getTime() - startTime.getTime()) / 1000 / 60)
        : null

      const startTimeStr = startTime.toLocaleString()
      const sessionId = ps.id
      commandAnnotations[`plug_start_${idx}`] = {
        type: 'line',
        xMin: startTime,
        xMax: startTime,
        borderColor: '#10b981',
        borderWidth: 3,
        enter: (ctx: unknown, _event: unknown) => {
          const annotation = (
            ctx as {
              element?: {
                label?: { options?: { content?: string | string[] } }
              }
            }
          ).element
          if (annotation?.label?.options) {
            annotation.label.options.content = [
              'Plugged in',
              '',
              `Session #${sessionId}`,
              startTimeStr,
            ]
          }
          return true
        },
        leave: (ctx: unknown, _event: unknown) => {
          const annotation = (
            ctx as {
              element?: {
                label?: { options?: { content?: string | string[] } }
              }
            }
          ).element
          if (annotation?.label?.options) {
            annotation.label.options.content = 'Plugged In'
          }
          return true
        },
        label: {
          display: true,
          content: 'Plugged In',
          position: '20%',
          backgroundColor: '#10b981',
          color: '#111827',
          font: { size: 10, weight: 'bold' as const },
          padding: 4,
        },
      }

      if (endTime) {
        const endTimeStr = endTime.toLocaleString()
        commandAnnotations[`plug_end_${idx}`] = {
          type: 'line',
          xMin: endTime,
          xMax: endTime,
          borderColor: '#f43f5e',
          borderWidth: 3,
          enter: (ctx: unknown, _event: unknown) => {
            const annotation = (
              ctx as {
                element?: {
                  label?: { options?: { content?: string | string[] } }
                }
              }
            ).element
            if (annotation?.label?.options) {
              annotation.label.options.content = [
                'Unplugged',
                '',
                `Session #${sessionId}`,
                endTimeStr,
                `Duration: ${duration} min`,
              ]
            }
            return true
          },
          leave: (ctx: unknown, _event: unknown) => {
            const annotation = (
              ctx as {
                element?: {
                  label?: { options?: { content?: string | string[] } }
                }
              }
            ).element
            if (annotation?.label?.options) {
              annotation.label.options.content = 'Unplugged'
            }
            return true
          },
          label: {
            display: true,
            content: 'Unplugged',
            position: '20%',
            backgroundColor: '#f43f5e',
            color: '#111827',
            font: { size: 10, weight: 'bold' as const },
            padding: 4,
          },
        }
      }
    })

    // Mark annotations as visible/hidden based on toggles
    Object.entries(commandAnnotations).forEach(([key, annotation]) => {
      if (key.startsWith('plug_')) {
        annotation.display = showPlugSessions
      } else if (key.startsWith('cmd_')) {
        // Extract command index and check if command type is visible
        const cmdIndex = parseInt(key.replace('cmd_', ''), 10)
        const cmd = commands[cmdIndex]
        annotation.display =
          showCommands && cmd && visibleCommandTypes.has(cmd.command_type)
      }
    })

    const datasets = [
      {
        label: 'Charge State Activity',
        data: chargeStateData,
        hidden: !showChargeState,
        borderColor: (ctx: { p0DataIndex?: number; dataIndex?: number }) => {
          const index = ctx.p0DataIndex ?? ctx.dataIndex ?? 0
          const point = chargeStateData[index]
          return point?.chargingState === 'Charging' ? '#4ade80' : '#1e40af'
        },
        backgroundColor: 'rgba(30, 64, 175, 0.3)',
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: (ctx: { dataIndex?: number }) => {
          const point = chargeStateData[ctx.dataIndex ?? 0]
          return point?.chargingState === 'Charging' ? '#4ade80' : '#1e40af'
        },
        showLine: true,
        fill: true,
        segment: {
          borderColor: (ctx: { p0DataIndex: number; p1DataIndex: number }) => {
            const point = chargeStateData[ctx.p0DataIndex]
            return point?.chargingState === 'Charging' ? '#4ade80' : '#1e40af'
          },
          backgroundColor: (ctx: {
            p0DataIndex: number
            p1DataIndex: number
          }) => {
            const point = chargeStateData[ctx.p0DataIndex]
            return point?.chargingState === 'Charging'
              ? 'rgba(74, 222, 128, 0.3)'
              : 'rgba(30, 64, 175, 0.3)'
          },
        },
        yAxisID: 'y',
      },
      {
        label: 'Charge Limit',
        data: chargeLimitData,
        hidden: !showChargeLimit,
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.2)',
        borderWidth: 2,
        stepped: true,
        fill: true,
        pointRadius: 3,
        yAxisID: 'y',
      },
    ]

    // Add charger power dataset (EVSE ghost vehicles — no battery_level)
    const chargerPowerData = charge_states
      .filter((cs) => cs.charger_power != null)
      .map((cs) => ({ x: new Date(cs.created), y: cs.charger_power as number }))

    if (chargerPowerData.length > 0) {
      datasets.push({
        label: 'Charger Power (kW)',
        data: chargerPowerData,
        hidden: !showChargerPower,
        borderColor: '#f97316',
        backgroundColor: 'rgba(249, 115, 22, 0.1)',
        borderWidth: 2,
        pointRadius: 3,
        fill: false,
        yAxisID: 'y3',
        stepped: false,
      })
    }

    // Add rate plan datasets
    const { userRateData, programRateData } = ratePlanTimelineData

    if (userRateData.length > 0) {
      datasets.push({
        label: 'User Rate Plan',
        data: userRateData,
        hidden: !showUserRates,
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        pointRadius: 2,
        fill: false,
        yAxisID: 'y1',
        stepped: false,
      })
    }

    if (programRateData.length > 0) {
      datasets.push({
        label: 'Program Rate Plan',
        data: programRateData,
        hidden: !showProgramRates,
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        borderWidth: 2,
        pointRadius: 2,
        fill: false,
        yAxisID: 'y1',
        stepped: false,
      })
    }

    // Add emissions dataset
    if (emissionsTimelineData.length > 0) {
      datasets.push({
        label: 'Emissions Intensity',
        data: emissionsTimelineData,
        hidden: !showEmissions,
        borderColor: '#10b981', // Green for emissions
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 2,
        pointRadius: 2,
        // pointHoverRadius: 4,
        // showLine: true,
        fill: false,
        yAxisID: 'y2',
        stepped: true, // Stepped line for emissions intensity levels
      })
    }

    return {
      datasets,
      annotations: { ...drEventAnnotations, ...commandAnnotations },
    }
  }, [
    data,
    showChargeState,
    showChargeLimit,
    showPlugSessions,
    showCommands,
    showUserRates,
    showProgramRates,
    showEmissions,
    showChargerPower,
    visibleCommandTypes,
    fetchParams,
    ratePlanTimelineData,
    emissionsTimelineData,
    drEventAnnotations,
  ])

  const chartOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      scales: {
        x: {
          type: 'time' as const,
          min: fetchParams?.start
            ? new Date(fetchParams.start).getTime()
            : undefined,
          max: fetchParams?.end
            ? new Date(fetchParams.end).getTime()
            : undefined,
          time: {
            displayFormats: {
              hour: 'MMM d, HH:mm',
              day: 'MMM d',
            },
          },
          title: {
            display: true,
            text: 'Time',
          },
        },
        y: {
          type: 'linear' as const,
          display: true,
          position: 'left' as const,
          min: 0,
          max: 100,
          title: {
            display: true,
            text: 'Battery %',
          },
        },
        y1: {
          type: 'linear' as const,
          display: true,
          position: 'right' as const,
          title: {
            display: true,
            text: 'Rate (¢/kWh)',
          },
          grid: {
            drawOnChartArea: false,
          },
        },
        y2: {
          type: 'linear' as const,
          display: false, // Hidden axis for emissions, shown in legend only
          position: 'right' as const,
          title: {
            display: false,
            text: 'Emissions Intensity',
          },
          grid: {
            drawOnChartArea: false,
          },
        },
        y3: {
          type: 'linear' as const,
          display: true,
          position: 'right' as const,
          min: 0,
          title: {
            display: true,
            text: 'Power (kW)',
          },
          grid: {
            drawOnChartArea: false,
          },
        },
      },
      plugins: {
        legend: {
          position: 'top' as const,
        },
        title: {
          display: true,
          text: 'Charge Debugging Timeline',
          font: { size: 16, weight: 'bold' as const },
        },
        tooltip: {
          callbacks: {
            label: (context: TooltipItem<'line'>) => {
              const label = context.dataset.label || ''
              const value = context.parsed.y

              if (label === 'Charge Limit') {
                return `${label}: ${value}%`
              }
              if (label === 'Charge State Activity') {
                const raw = context.raw as { chargingState?: string | null }
                return [
                  `Battery: ${value}%`,
                  `Charging State: ${raw.chargingState ?? 'N/A'}`,
                ]
              }
              if (label === 'User Rate Plan' || label === 'Program Rate Plan') {
                return `${label}: ${value?.toFixed(2)}¢/kWh`
              }
              if (label === 'Emissions Intensity') {
                const raw = context.raw as { intensity?: number | null }
                return (
                  `${label}: ${raw.intensity ?? value}` +
                  (raw.intensity ? ' intensity' : '')
                )
              }
              if (label === 'Charger Power (kW)') {
                return `Charger Power: ${value?.toFixed(2)} kW`
              }
              return label
            },
          },
        },
        annotation: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          annotations: (chartData?.annotations ?? {}) as any,
        },
      },
    }
  }, [chartData?.annotations, fetchParams])

  const commandTypes = data?.commands
    ? [...new Set(data.commands.map((c) => c.command_type))]
    : []

  return (
    <div className="w-full p-4">
      <h3 className="mb-4 text-lg font-semibold">Charge Debugging</h3>

      {/* Timezone info blurb */}
      <div className="mb-4 rounded-md bg-yellow-50 p-3 border border-yellow-200">
        <div className="text-yellow-800 text-sm">
          <strong>Timezone:</strong> All times are displayed in your browser's
          local timezone — <strong>{getBrowserTimezoneLabel()}</strong>. Data is
          stored as UTC on the server. If you're debugging a device in a
          different timezone, keep this in mind when interpreting timestamps.
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-4">
        {/* Device mode selector */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">
            Device Type
          </label>
          <div className="flex rounded-md border border-gray-300 overflow-hidden">
            <button
              onClick={() => {
                setDeviceMode('vehicle')
                setChargerId('')
              }}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                deviceMode === 'vehicle'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Vehicle
            </button>
            <button
              onClick={() => {
                setDeviceMode('charger')
                setVehicleId('')
              }}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                deviceMode === 'charger'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Charger
            </button>
          </div>
        </div>

        {deviceMode === 'vehicle' ? (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Vehicle</label>
            <select
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">Select a vehicle</option>
              {vehicles?.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.car?.display_name ||
                    v.car?.car_model?.friendly_name ||
                    `Vehicle ${v.id}`}{' '}
                  (ID: {v.id})
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Charger</label>
            <select
              value={chargerId}
              onChange={(e) => setChargerId(e.target.value)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">Select a charger</option>
              {chargers?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || `Charger ${c.id}`} (ID: {c.id})
                  {c.authentication_inactive ? ' — inactive' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">
            Start Date
          </label>
          <input
            type="datetime-local"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">End Date</label>
          <input
            type="datetime-local"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          />
        </div>

        <button
          onClick={handleFetch}
          disabled={
            (deviceMode === 'vehicle' ? !vehicleId : !chargerId) || isLoading
          }
          className="rounded-md bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          {isLoading ? 'Loading...' : 'Fetch Data'}
        </button>
      </div>

      {/* Performance Optimization Notice */}
      <div className="mb-4 rounded-md bg-blue-50 p-3 border border-blue-200">
        <div className="flex items-center gap-2">
          <div className="text-blue-800 text-sm">
            <strong>Performance Tip:</strong> Use the visibility toggles below
            to control what data is fetched. Only enabled data types will be
            requested from the server, improving load times.
          </div>
        </div>
      </div>

      {/* Pre-fetch Data Selection */}
      <div className="mb-4 rounded-md bg-gray-50 p-3">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Select Data to Fetch (Performance Optimization)
        </div>
        <div className="text-xs text-gray-600 mb-2">
          Toggle these before clicking "Fetch Data" to only load the data you
          need.
        </div>
        <div className="flex flex-wrap gap-3">
          <ToggleButton
            active={showChargeState}
            onClick={() => setShowChargeState(!showChargeState)}
            color="#1e40af"
            icon="line"
          >
            Charge States
          </ToggleButton>
          <ToggleButton
            active={showPlugSessions}
            onClick={() => setShowPlugSessions(!showPlugSessions)}
            color="#7c3aed"
            icon="line"
          >
            Plug Sessions
          </ToggleButton>
          <ToggleButton
            active={showCommands}
            onClick={() => setShowCommands(!showCommands)}
            color="#6b7280"
            icon="dashed"
          >
            Commands
          </ToggleButton>
          <ToggleButton
            active={showUserRates}
            onClick={() => setShowUserRates(!showUserRates)}
            color="#3b82f6"
            icon="line"
          >
            User Rates
          </ToggleButton>
          <ToggleButton
            active={showProgramRates}
            onClick={() => setShowProgramRates(!showProgramRates)}
            color="#ef4444"
            icon="line"
          >
            Program Rates
          </ToggleButton>
          <ToggleButton
            active={showEmissions}
            onClick={() => setShowEmissions(!showEmissions)}
            color="#10b981"
            icon="line"
          >
            Emissions
          </ToggleButton>
          <ToggleButton
            active={showDemandResponse}
            onClick={() => setShowDemandResponse(!showDemandResponse)}
            color="#a855f7"
            icon="line"
          >
            DR Events
          </ToggleButton>
          <ToggleButton
            active={showChargerPower}
            onClick={() => setShowChargerPower(!showChargerPower)}
            color="#f97316"
            icon="line"
          >
            Charger Power
          </ToggleButton>
        </div>
      </div>

      {isLoading && (
        <LoadingText variant="body4" className="my-4">
          Loading charge debugging data
        </LoadingText>
      )}

      {!isLoading && hasFetched && !data && (
        <p className="my-4 text-sm text-red-600">
          No data found for the selected vehicle and time range.
        </p>
      )}

      {chartData && (
        <>
          <div className="mt-4 h-[500px] rounded-lg bg-white p-4 shadow-sm">
            <Line data={chartData} options={chartOptions} />
          </div>

          <div className="mt-4 rounded-md bg-gray-50 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Toggle Data Visibility
            </div>
            <div className="flex flex-wrap gap-3">
              <ToggleButton
                active={showChargeState}
                onClick={() => setShowChargeState(!showChargeState)}
                color="#1e40af"
                icon="circle"
              >
                Charge State
              </ToggleButton>
              <ToggleButton
                active={showChargeLimit}
                onClick={() => setShowChargeLimit(!showChargeLimit)}
                color="#f59e0b"
                icon="line"
              >
                Charge Limit
              </ToggleButton>
              <ToggleButton
                active={showPlugSessions}
                onClick={() => setShowPlugSessions(!showPlugSessions)}
                color="#10b981"
                icon="line"
              >
                Plug Sessions
              </ToggleButton>
              <ToggleButton
                active={showCommands}
                onClick={() => setShowCommands(!showCommands)}
                color="#6b7280"
                icon="dashed"
              >
                All Commands
              </ToggleButton>
              <ToggleButton
                active={showUserRates}
                onClick={() => setShowUserRates(!showUserRates)}
                color="#3b82f6"
                icon="line"
              >
                User Rates
              </ToggleButton>
              <ToggleButton
                active={showProgramRates}
                onClick={() => setShowProgramRates(!showProgramRates)}
                color="#ef4444"
                icon="line"
              >
                Program Rates
              </ToggleButton>
              <ToggleButton
                active={showEmissions}
                onClick={() => setShowEmissions(!showEmissions)}
                color="#10b981"
                icon="line"
              >
                Emissions Intensity
              </ToggleButton>
              <ToggleButton
                active={showDemandResponse}
                onClick={() => setShowDemandResponse(!showDemandResponse)}
                color="#a855f7"
                icon="line"
              >
                DR Events
              </ToggleButton>
              <ToggleButton
                active={showChargerPower}
                onClick={() => setShowChargerPower(!showChargerPower)}
                color="#f97316"
                icon="line"
              >
                Charger Power
              </ToggleButton>
            </div>
            {showCommands && commandTypes.length > 0 && (
              <div className="mt-3 border-t border-gray-200 pt-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Command Types
                </div>
                <div className="flex flex-wrap gap-2">
                  {commandTypes.map((type) => (
                    <ToggleButton
                      key={type}
                      active={visibleCommandTypes.has(type)}
                      onClick={() => {
                        const newSet = new Set(visibleCommandTypes)
                        if (newSet.has(type)) {
                          newSet.delete(type)
                        } else {
                          newSet.add(type)
                        }
                        setVisibleCommandTypes(newSet)
                      }}
                      color={getCommandColor(type)}
                      icon="dashed"
                      small
                    >
                      {getCommandLabel(type)}
                    </ToggleButton>
                  ))}
                </div>
              </div>
            )}
          </div>

          {data && (
            <div className="mt-6">
              <h4 className="mb-3 font-semibold">Summary</h4>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
                <SummaryCard
                  title="Plug Sessions"
                  value={data.plug_sessions.length}
                />
                <SummaryCard
                  title="Charge State Changes"
                  value={data.charge_states.length}
                />
                <SummaryCard title="Commands" value={data.commands.length} />
                <SummaryCard
                  title="Charge Schedules"
                  value={data.charge_schedules.length}
                />
                <SummaryCard
                  title="Trips"
                  value={data.charge_schedule_trips.length}
                />
                <SummaryCard
                  title="User Rate Hours"
                  value={data.user_rate_plan.length}
                />
                <SummaryCard
                  title="Program Rate Hours"
                  value={data.program_rate_plan.length}
                />
                <SummaryCard
                  title="Emissions Periods"
                  value={data.emissions_data.length}
                />
                <SummaryCard
                  title="DR Events"
                  value={data.demand_response_events.length}
                />
              </div>

              {data.commands.length > 0 && (
                <div className="mt-6">
                  <h4 className="mb-3 font-semibold">Commands</h4>
                  <CommandsTable commands={data.commands} />
                </div>
              )}

              {data.plug_sessions.length > 0 && (
                <div className="mt-6">
                  <h4 className="mb-3 font-semibold">Plug Sessions</h4>
                  <PlugSessionsTable sessions={data.plug_sessions} />
                </div>
              )}

              {data.charge_schedules.length > 0 && (
                <div className="mt-6">
                  <h4 className="mb-3 font-semibold">
                    Historical Charge Schedules
                  </h4>
                  <ChargeSchedulesTable schedules={data.charge_schedules} />
                </div>
              )}

              {data.charge_schedule_trips.length > 0 && (
                <div className="mt-6">
                  <h4 className="mb-3 font-semibold">Charge Schedule Trips</h4>
                  <TripsTable trips={data.charge_schedule_trips} />
                </div>
              )}

              {data.demand_response_events.length > 0 && (
                <div className="mt-6">
                  <h4 className="mb-3 font-semibold">Demand Response Events</h4>
                  <DemandResponseEventsTable
                    events={data.demand_response_events}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function SummaryCard({ title, value }: { title: string; value: number }) {
  return (
    <div className="rounded-lg bg-gray-50 p-4 text-center">
      <div className="text-2xl font-bold text-blue-500">{value}</div>
      <div className="text-sm text-gray-500">{title}</div>
    </div>
  )
}

function ToggleButton({
  active,
  onClick,
  color,
  icon,
  small,
  children,
}: {
  active: boolean
  onClick: () => void
  color: string
  icon: 'line' | 'circle' | 'dashed'
  small?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-all ${
        active
          ? 'border-gray-300 bg-white shadow-sm'
          : 'border-transparent bg-gray-200 opacity-50'
      } ${small ? 'px-2 py-1 text-xs' : ''}`}
    >
      <div
        className={`${
          icon === 'circle' ? 'h-2.5 w-2.5 rounded-full' : 'h-1 w-4 rounded-sm'
        } ${icon === 'dashed' ? 'border border-dashed' : ''}`}
        style={{
          backgroundColor: active ? color : '#9ca3af',
          borderColor: icon === 'dashed' ? color : undefined,
        }}
      />
      <span className={active ? 'text-gray-800' : 'text-gray-500'}>
        {children}
      </span>
    </button>
  )
}

function CommandsTable({ commands }: { commands: ChargeScheduleCommandLog[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Time
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Command Type
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Input
            </th>
          </tr>
        </thead>
        <tbody>
          {[...commands]
            .toSorted(
              (a, b) =>
                new Date(a.created).getTime() - new Date(b.created).getTime(),
            )
            .map((cmd) => (
              <tr key={cmd.id} className="hover:bg-gray-50">
                <td className="border-b border-gray-200 px-3 py-2">
                  {new Date(cmd.created).toLocaleString()}
                </td>
                <td className="border-b border-gray-200 px-3 py-2">
                  <span
                    className="rounded px-2 py-0.5 text-xs text-white"
                    style={{
                      backgroundColor: getCommandColor(cmd.command_type),
                    }}
                  >
                    {getCommandLabel(cmd.command_type)}
                  </span>
                </td>
                <td className="border-b border-gray-200 px-3 py-2">
                  {cmd.command_input && (
                    <pre className="m-0 max-w-[300px] overflow-auto text-[11px]">
                      {JSON.stringify(JSON.parse(cmd.command_input), null, 2)}
                    </pre>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  )
}

function PlugSessionsTable({ sessions }: { sessions: PlugSession[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              ID
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Start Time
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              End Time
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Duration
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Location ID
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Home ID
            </th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((session) => {
            const start = new Date(session.start_time)
            const end = session.end_time ? new Date(session.end_time) : null
            const duration = end
              ? Math.round((end.getTime() - start.getTime()) / 1000 / 60)
              : null
            return (
              <tr key={session.id} className="hover:bg-gray-50">
                <td className="border-b border-gray-200 px-3 py-2">
                  {session.id}
                </td>
                <td className="border-b border-gray-200 px-3 py-2">
                  {start.toLocaleString()}
                </td>
                <td className="border-b border-gray-200 px-3 py-2">
                  {end ? end.toLocaleString() : '—'}
                </td>
                <td className="border-b border-gray-200 px-3 py-2">
                  {duration ? `${duration} min` : 'Ongoing'}
                </td>
                <td className="border-b border-gray-200 px-3 py-2">
                  {session.location ?? '—'}
                </td>
                <td className="border-b border-gray-200 px-3 py-2">
                  {session.home ?? '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ChargeSchedulesTable({
  schedules,
}: {
  schedules: ChargeDebuggingChargeSchedule[]
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              History Date
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Battery Target (Min/Max)
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Money Saving
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Save the Planet
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Precondition
            </th>
          </tr>
        </thead>
        <tbody>
          {schedules
            .toSorted(
              (a, b) =>
                new Date(b.history_date).getTime() -
                new Date(a.history_date).getTime(),
            )
            .map((schedule) => (
              <tr
                key={schedule.history_date.toString()}
                className="hover:bg-gray-50"
              >
                <td className="border-b border-gray-200 px-3 py-2">
                  {new Date(schedule.history_date).toLocaleString()}
                </td>
                <td className="border-b border-gray-200 px-3 py-2">
                  {schedule.battery_target_min}% / {schedule.battery_target_max}
                  %
                </td>
                <td className="border-b border-gray-200 px-3 py-2">
                  {schedule.money_saving_plan_enabled ? 'Yes' : 'No'}
                </td>
                <td className="border-b border-gray-200 px-3 py-2">
                  {schedule.save_the_planet_plan_enabled ? 'Yes' : 'No'}
                </td>
                <td className="border-b border-gray-200 px-3 py-2">
                  {schedule.should_precondition ? 'Yes' : 'No'}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  )
}

function DemandResponseEventsTable({
  events,
}: {
  events: DemandResponseEventData[]
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-purple-50">
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              ID
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Name
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Type
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Start
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              End
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Enrolled
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Participated
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Opt Choice
            </th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr key={event.id} className="hover:bg-gray-50">
              <td className="border-b border-gray-200 px-3 py-2">{event.id}</td>
              <td className="border-b border-gray-200 px-3 py-2">
                {event.name ?? '—'}
              </td>
              <td className="border-b border-gray-200 px-3 py-2">
                <div className="text-xs">
                  <span className="font-medium">
                    {event.dispatch_event_type ?? event.event_type}
                  </span>
                </div>
              </td>
              <td className="border-b border-gray-200 px-3 py-2">
                {new Date(event.start_datetime).toLocaleString()}
              </td>
              <td className="border-b border-gray-200 px-3 py-2">
                {new Date(event.end_datetime).toLocaleString()}
              </td>
              <td className="border-b border-gray-200 px-3 py-2">
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${
                    event.enrolled
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {event.enrolled ? 'Yes' : 'No'}
                </span>
              </td>
              <td className="border-b border-gray-200 px-3 py-2">
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${
                    event.participated
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {event.participated ? 'Yes' : 'No'}
                </span>
              </td>
              <td className="border-b border-gray-200 px-3 py-2">
                {event.user_opt_choice ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TripsTable({ trips }: { trips: ChargeScheduleTrip[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              ID
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Departure (UTC)
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Local Date/Time
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Battery Target (Min/Max)
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Allow Control
            </th>
            <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">
              Precondition
            </th>
          </tr>
        </thead>
        <tbody>
          {trips.map((trip) => (
            <tr key={trip.id} className="hover:bg-gray-50">
              <td className="border-b border-gray-200 px-3 py-2">{trip.id}</td>
              <td className="border-b border-gray-200 px-3 py-2">
                {new Date(trip.departure_datetime_utc).toLocaleString()}
              </td>
              <td className="border-b border-gray-200 px-3 py-2">
                {trip.date_local_timezone} {trip.time_local_timezone}
              </td>
              <td className="border-b border-gray-200 px-3 py-2">
                {trip.battery_target_min ?? '—'}% /{' '}
                {trip.battery_target_max ?? '—'}%
              </td>
              <td className="border-b border-gray-200 px-3 py-2">
                {trip.allow_charging_control ? 'Yes' : 'No'}
              </td>
              <td className="border-b border-gray-200 px-3 py-2">
                {trip.should_precondition ? 'Yes' : 'No'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
